import { Matcher, MetricType, ParserTerms, parser } from '@chronosphereio/editors/prometheus';
import { SyntaxNode, SyntaxNodeRef } from '@lezer/common';
import { invert, pick } from 'lodash';

const {
  AggregateExpr,
  AggregateOp,
  AggregateModifier,
  Duration,
  FunctionCall,
  FunctionIdentifier,
  FunctionCallBody,
  Identifier,
  GroupingLabels,
  LabelMatcher,
  LabelMatchers,
  LabelName,
  MatrixSelector,
  NumberLiteral,
  QuotedLabelName,
  TemplateVariable,
  VectorSelector,
  By,
} = ParserTerms;

/**
 * Checks if a metric or label name is Prometheus legacy compatible.
 * Legacy names only contain: [a-zA-Z0-9_:]
 */
function isLegacyCompatible(name: string): boolean {
  return /^[a-zA-Z_:][a-zA-Z0-9_:]+$/.test(name);
}

/**
 * Formats a label name for PromQL query generation.
 * Non-legacy names are wrapped in quotes: "label.name"
 */
function formatLabelName(labelName: string): string {
  if (!labelName) return '';
  if (isLegacyCompatible(labelName)) {
    return labelName;
  }
  return `"${labelName}"`;
}

/**
 * Removes quotes from a metric or label name if present.
 */
function unquoteName(name: string): string {
  // Remove surrounding quotes if present
  return name.replace(/^["'`]|["'`]$/g, '');
}

/**
 * Metric metadata.
 */
export type MetricMetadata = {
  metricType: MetricType | undefined;
  isClassicHistogramMetric: boolean;
};

export const UNKNOWN_METRIC_METADATA = {
  metricType: undefined,
  isClassicHistogramMetric: false,
};

/**
 * The state of the PromQL builder.
 */
export type BuilderState = {
  metric: string;
  filters: Filter[];
  range?: Range;
  calculation?: Calculation;
};

export type Filter = {
  label: string;
  operator: FilterOperator;
  values: string[];
};

export const FILTER_OPERATORS = { Equal: '=', NotEqual: '!=', RegexEqual: '=~', RegexNotEqual: '!~' } as const;
export type FilterOperator = (typeof FILTER_OPERATORS)[keyof typeof FILTER_OPERATORS];
export const { Equal, NotEqual, RegexEqual, RegexNotEqual } = FILTER_OPERATORS;

export type Range = {
  functionName: RangeFunction;
  interval: string;
};

const RANGE_FUNCTIONS = ['rate', 'increase', 'sum_per_second', 'sum_over_time'] as const;
export type RangeFunction = (typeof RANGE_FUNCTIONS)[number];

export const RANGE_FUNCTION_TO_DEFAULT_INTERVAL: Record<RangeFunction, string> = {
  rate: '$__rate_interval',
  increase: '$__interval',
  sum_per_second: '$__interval',
  sum_over_time: '$__interval',
};

export const DEFAULT_CUMULATIVE_RANGE: Range = {
  functionName: 'rate',
  interval: RANGE_FUNCTION_TO_DEFAULT_INTERVAL['rate'],
};

export const DEFAULT_DELTA_RANGE: Range = {
  functionName: 'sum_over_time',
  interval: RANGE_FUNCTION_TO_DEFAULT_INTERVAL['sum_over_time'],
};

export const METRIC_TYPE_TO_DEFAULT_RANGE: Record<MetricType, Range | undefined> = {
  CUMULATIVE_COUNTER: DEFAULT_CUMULATIVE_RANGE,
  CUMULATIVE_EXPONENTIAL_HISTOGRAM: DEFAULT_CUMULATIVE_RANGE,
  DELTA_COUNTER: DEFAULT_DELTA_RANGE,
  DELTA_EXPONENTIAL_HISTOGRAM: DEFAULT_DELTA_RANGE,
  GAUGE: undefined,
  MEASUREMENT: undefined,
  INVALID: undefined,
};

export function getRecommendedRange(metricType?: MetricType): Range | undefined {
  if (!metricType) return undefined;
  return METRIC_TYPE_TO_DEFAULT_RANGE[metricType];
}

export function metricTypeUsesRangeFunction(metricType?: MetricType): boolean {
  if (!metricType) return false;
  return METRIC_TYPE_TO_DEFAULT_RANGE[metricType] !== undefined;
}

export type Calculation = Aggregation | Percentile | HistogramAverage | HistogramCount;

export type Aggregation = {
  kind: AggregationKind;
  labels: string[];
};

export const AGGREGATION_KINDS = ['sum', 'average', 'max', 'min', 'count'] as const;
export type AggregationKind = (typeof AGGREGATION_KINDS)[number];

export type Percentile = {
  kind: 'percentile';
  quantile: number;
  labels?: string[]; // `undefined` means don't include `sum`
};

export type HistogramAverage = {
  kind: 'histogramAverage';
  labels?: string[]; // `undefined` means don't include `sum`
};

export type HistogramCount = {
  kind: 'histogramCount';
  labels?: string[]; // `undefined` means don't include `sum`
};

export const CALCULATION_KIND_TO_FUNCTION: Record<Calculation['kind'], string> = {
  sum: 'sum',
  average: 'avg',
  max: 'max',
  min: 'min',
  count: 'count',
  percentile: 'histogram_quantile',
  histogramAverage: 'histogram_avg',
  histogramCount: 'histogram_count',
};

const FUNCTION_TO_CALCULATION_KIND = invert(CALCULATION_KIND_TO_FUNCTION);

const AGGREGATION_FUNCTIONS = Object.values(pick(CALCULATION_KIND_TO_FUNCTION, AGGREGATION_KINDS));
const HISTOGRAM_KINDS = ['percentile', 'histogramAverage', 'histogramCount'];
const HISTOGRAM_FUNCTIONS = Object.values(pick(CALCULATION_KIND_TO_FUNCTION, HISTOGRAM_KINDS));

export function isValidFilter(filter: Filter): boolean {
  return filter.label !== '' && filter.values.length > 0;
}

export function hasRegexOperator(filter: Filter): boolean {
  return isRegexOperator(filter.operator);
}

export function isRegexOperator(operator: FilterOperator) {
  return operator === FILTER_OPERATORS.RegexEqual || operator === FILTER_OPERATORS.RegexNotEqual;
}

export function isCumulativeType(metricType?: MetricType) {
  return metricType === 'CUMULATIVE_COUNTER' || metricType === 'CUMULATIVE_EXPONENTIAL_HISTOGRAM';
}

export function isDeltaType(metricType?: MetricType) {
  return metricType === 'DELTA_COUNTER' || metricType === 'DELTA_EXPONENTIAL_HISTOGRAM';
}

export function isHistogramType(metricType?: MetricType) {
  return metricType === 'CUMULATIVE_EXPONENTIAL_HISTOGRAM' || metricType === 'DELTA_EXPONENTIAL_HISTOGRAM';
}

export function isAggregation(c?: Calculation): c is Aggregation {
  return c ? isAggregationKind(c.kind) : false;
}

export function isPercentile(c?: Calculation): c is Percentile {
  return c ? isPercentileKind(c.kind) : false;
}

export function isHistogramAverage(c?: Calculation): c is HistogramAverage {
  return c ? isHistogramAverageKind(c.kind) : false;
}

export function isHistogramCount(c?: Calculation): c is HistogramCount {
  return c ? isHistogramCountKind(c.kind) : false;
}

export function isAggregationKind(kind?: string): kind is AggregationKind {
  return kind ? AGGREGATION_KINDS.includes(kind as AggregationKind) : false;
}

export function isPercentileKind(kind?: string): kind is Percentile['kind'] {
  return kind ? kind === 'percentile' : false;
}

export function isHistogramAverageKind(kind?: string): kind is HistogramAverage['kind'] {
  return kind ? kind === 'histogramAverage' : false;
}

export function isHistogramCountKind(kind?: string): kind is HistogramCount['kind'] {
  return kind ? kind === 'histogramCount' : false;
}

export function isClassicHistogramBucketMetric(metric?: string, isClassicHistogramMetric?: boolean) {
  return isClassicHistogramMetric && metric?.endsWith('_bucket');
}

export function isHistogramMetric(metric: string, metricMetadata: MetricMetadata) {
  const { metricType, isClassicHistogramMetric } = metricMetadata;
  return isClassicHistogramBucketMetric(metric, isClassicHistogramMetric) || isHistogramType(metricType);
}

/**
 * PromQL => BuilderState.
 *
 * The parser requires knowing the state of isClassicHistogramMetric.
 */
export function parseQuery(query: string, { isClassicHistogramMetric }: MetricMetadata): BuilderState {
  const builderState: BuilderState = { metric: '', filters: [] };

  if (query === '') return builderState;

  const { metric, filters } = parseQueryForMetricAndFilters(query);
  builderState.metric = metric;
  builderState.filters = filters;

  const ast = parser.parse(query);

  let rangeFunction;
  let rangeInterval;

  // If multiple vector selectors, use the first one.
  let hasVisitedVectorSelector = false;

  ast.iterate({
    enter: (n) => {
      if (hasVisitedVectorSelector) return false;

      if (n.type.is(AggregateExpr)) {
        const aggregation = getAggregation(query, n);
        if (aggregation) {
          if (
            isPercentile(builderState.calculation) ||
            isHistogramAverage(builderState.calculation) ||
            isHistogramCount(builderState.calculation)
          ) {
            if (isClassicHistogramMetric) {
              // Classic histograms must include the `le` label, so we don't expose it as an editable label.
              builderState.calculation.labels = aggregation.labels.filter((l) => l != 'le');
            } else {
              builderState.calculation.labels = aggregation.labels;
            }
          } else {
            builderState.calculation = aggregation;
          }
        }
      }

      if (n.type.is(FunctionCall)) {
        const functionName = getFunctionName(n.node, query);
        switch (functionName) {
          case 'histogram_quantile':
            builderState.calculation = getHistogramQuantile(query, n);
            return;
          case 'histogram_avg':
            builderState.calculation = { kind: 'histogramAverage' };
            return;
          case 'histogram_count':
            builderState.calculation = { kind: 'histogramCount' };
            return;
          default:
            // TODO: Range elements should come from a single expression.
            rangeFunction = getRangeFunction(query, n);
        }
      }

      if (n.type.is(MatrixSelector)) {
        rangeInterval = getRangeInterval(query, n);
      }

      if (n.type.is(VectorSelector)) {
        hasVisitedVectorSelector = true;
      }
    },
  });

  if (rangeFunction && rangeInterval) {
    builderState.range = {
      functionName: rangeFunction,
      interval: rangeInterval,
    };
  }

  return builderState;
}

/**
 * Extract all metric names from a PromQL query.
 *
 * @example
 * extractMetricsFromPromQuery('requests_errors{handler="getPayments"} / requests_total{handler="getPayments"}')
 * // Returns ['requests_errors', 'requests_total']
 */
export function extractMetricsFromPromQuery(query: string): string[] {
  const results = extractMetricAndLabelsFromPromQuery(query);
  // Filter out empty metric names
  const metrics = results.map((r) => r.metric).filter((m) => m !== '');
  // Dedupe
  return [...new Set(metrics)];
}

/**
 * Extract metrics and labels from a PromQL query.
 * Walks the AST and gets the metric name and label names from each VectorSelector.
 *
 * @example
 * extractMetricAndLabelsFromPromQuery('requests_total{method="POST", handler="getPayments"}')
 * // Returns [{ metric: 'requests_total', labels: ['method', 'handler'] }]
 */
export function extractMetricAndLabelsFromPromQuery(query: string): Array<{ metric: string; labels: string[] }> {
  const results: Array<{ metric: string; labels: string[] }> = [];

  try {
    const ast = parser.parse(query);
    ast.iterate({
      enter: (node) => {
        if (node.type.is(VectorSelector)) {
          const metric = getMetricName(query, node);
          const filters = getFilters(query, node);
          const labels = filters.map((f) => f.label);

          results.push({ metric, labels });
        }
      },
    });
  } catch {
    // If parsing fails, return empty array.
  }

  return results;
}

/**
 * Parse the metric and filters from a query.
 */
export function parseQueryForMetricAndFilters(query: string): {
  metric: BuilderState['metric'];
  filters: BuilderState['filters'];
} {
  let metric: string = '';
  let filters: Filter[] = [];

  const ast = parser.parse(query);

  // If multiple vector selectors, use the first one.
  let hasVisitedVectorSelector = false;

  ast.iterate({
    enter: (n) => {
      if (hasVisitedVectorSelector) return false;

      if (n.type.is(VectorSelector)) {
        metric = getMetricName(query, n);
        filters = getFilters(query, n);

        hasVisitedVectorSelector = true;
      }
    },
  });

  return { metric, filters };
}

/**
 * Get metric name from a VectorSelector node.
 * First, checks for a metric name in an Identifier nodes (ex. `my_metric{...}`).
 * Then, checks for a metric name in a QuotedLabelName node (ex. `{"my_metric"}`), which is part of Prom 3.0.
 */
function getMetricName(query: string, node: SyntaxNodeRef): BuilderState['metric'] {
  // First, check for an Identifier node
  const identifierNode = node.node.getChild(Identifier);
  if (identifierNode) {
    return query.substring(identifierNode.from, identifierNode.to);
  }

  // Next, check for a QuotedLabelName node
  const labelMatchers = node.node.getChild(LabelMatchers);
  if (!labelMatchers) return '';

  const quotedLabelName = labelMatchers.getChild(QuotedLabelName);
  if (!quotedLabelName) return '';

  const quotedName = query.substring(quotedLabelName.from, quotedLabelName.to);
  return unquoteName(quotedName);
}

/**
 * Get filters from VectorSelector node.
 */
function getFilters(query: string, node: SyntaxNodeRef): BuilderState['filters'] {
  const filters: Filter[] = [];

  node.node.cursor().iterate((n) => {
    if (n.type.is(LabelMatcher)) {
      const matcher = query.substring(n.from, n.to);
      const filter = parseLabelMatcher(matcher);
      if (filter) {
        filters.push(filter);
      }
    }
  });

  return filters;
}

/**
 * PromQL label matcher => Filter.
 */
function parseLabelMatcher(matcher: string): Filter | undefined {
  // TODO: What happens if label value includes these characters?
  const parts = matcher.split(/([=!~]+)/);

  const label = parts[0]?.trim();
  const operator = parts[1]?.trim() as FilterOperator;
  const value = parts[2]?.trim().replace(/["']/g, '');

  // Allow value to be empty string because that is valid PromQL
  if (!label || !operator || value === undefined) return;

  // Remove starting and closing parentheses
  let valueWithoutParens = value;
  if (value.startsWith('(') && value.endsWith(')')) {
    valueWithoutParens = value.slice(1, -1);
  }

  const values = valueWithoutParens.split('|').map((v) => v.trim());

  return {
    label: unquoteName(label), // Unquote label name for storage
    operator,
    values,
  };
}

/**
 * Get aggregation from AggregateExpr node.
 */
function getAggregation(query: string, node: SyntaxNodeRef): Aggregation | undefined {
  const typeNode = node.node.getChild(AggregateOp);
  if (!typeNode) return;

  const functionName = query.substring(typeNode.from, typeNode.to);
  const kind = FUNCTION_TO_CALCULATION_KIND[functionName] as AggregationKind;

  // TODO: Replace with AggregateExpr validation.
  if (!kind || !AGGREGATION_KINDS.includes(kind)) return;

  // TODO: Deal with by / without.
  const modifierNode = node.node.getChild(AggregateModifier);
  if (!modifierNode) return { kind, labels: [] };

  const groupingNodes = modifierNode.node.getChild(GroupingLabels);
  if (!groupingNodes) return { kind, labels: [] };

  const labels: string[] = [];
  // Iterate through children in source order to preserve label order
  // Handle both regular label names and quoted label names (Prom 3.0)
  const cursor = groupingNodes.node.cursor();
  for (let hasNext = cursor.next(); hasNext; hasNext = cursor.nextSibling()) {
    if (cursor.type.id === LabelName) {
      labels.push(query.substring(cursor.from, cursor.to));
    } else if (cursor.type.id === QuotedLabelName) {
      const quotedName = query.substring(cursor.from, cursor.to);
      labels.push(unquoteName(quotedName));
    }
  }

  return {
    kind,
    labels,
  };
}

/**
 * Get the function name from a FunctionCall node.
 */
export function getFunctionName(node: SyntaxNode, query: string) {
  const identifierNode = node.getChild(ParserTerms.FunctionIdentifier);
  if (!identifierNode) return;
  return query.substring(identifierNode.from, identifierNode.to);
}

/**
 * Get histogram quantile calculation from a FunctionCall node.
 */
function getHistogramQuantile(query: string, node: SyntaxNodeRef): Percentile | undefined {
  const callNode = node.node.getChild(FunctionCallBody);
  if (!callNode) return;

  const quantileNode = callNode.node.getChild(NumberLiteral);
  if (!quantileNode) return;

  return {
    kind: 'percentile',
    quantile: parseFloat(query.substring(quantileNode.from, quantileNode.to)),
  };
}

/**
 * Get range function from FunctionCall node.
 */
function getRangeFunction(query: string, node: SyntaxNodeRef): RangeFunction | undefined {
  const functionNode = node.node.getChild(FunctionIdentifier);
  if (!functionNode) return;

  const functionName = query.substring(functionNode.from, functionNode.to) as RangeFunction;
  // TODO: Replace with FunctionCall validation.
  if (!RANGE_FUNCTIONS.includes(functionName)) return;

  return functionName;
}

/**
 * Get range interval from MatrixSelector node.
 */
function getRangeInterval(query: string, node: SyntaxNodeRef): string | undefined {
  const durationNode = node.node.getChild(Duration);
  const variableNode = node.node.getChild(TemplateVariable);
  const intervalNode = durationNode ?? variableNode;
  if (!intervalNode) return;
  return query.substring(intervalNode.from, intervalNode.to);
}

/**
 * BuilderState => PromQL.
 *
 * The generator requires knowing the state of isClassicHistogramMetric.
 */
export function generateQuery(builderState: BuilderState, metadata?: MetricMetadata): string {
  let query = '';

  const { metric, filters, range, calculation } = builderState;
  const { isClassicHistogramMetric } = metadata ?? {};

  // Build the metric selector (metric name + filters)
  if (metric || filters.length > 0) {
    query = buildMetricSelector(metric, filters);
  }

  if (range) {
    query = applyRange(query, range);
  }

  if (calculation) {
    if (isAggregation(calculation)) {
      query = applyAggregation(query, calculation);
    }

    if (isPercentile(calculation)) {
      const { labels, quantile } = calculation;

      // Classic histograms must include the `le` label, so we don't expose it as an editable label.
      const labelsToInclude = isClassicHistogramMetric ? ['le', ...(labels ?? [])] : labels;

      if (labelsToInclude) {
        query = applyAggregation(query, { kind: 'sum', labels: labelsToInclude });
      }

      query = `histogram_quantile(${quantile}, ${query})`;
    }

    if (isHistogramAverage(calculation) || isHistogramCount(calculation)) {
      const { kind, labels } = calculation;

      if (labels) {
        query = applyAggregation(query, { kind: 'sum', labels });
      }

      const functionName = CALCULATION_KIND_TO_FUNCTION[kind];
      query = `${functionName}(${query})`;
    }
  }

  return query;
}

/**
 * Build a metric selector combining metric name and filters.
 * Handles both legacy and non-legacy metric names correctly.
 */
function buildMetricSelector(metric: string, filters: BuilderState['filters']): string {
  const isNonLegacyMetric = metric && !isLegacyCompatible(metric);

  // For legacy metrics or no metric, use the traditional format
  if (!isNonLegacyMetric) {
    if (filters.length === 0) {
      return metric || '';
    }
    const formattedFilters = filters
      .map((f) => `${formatLabelName(f.label)}${f.operator}"${f.values.join('|')}"`)
      .join(', ');
    return metric ? `${metric}{${formattedFilters}}` : `{${formattedFilters}}`;
  }

  // For non-legacy metrics, use quoted metric name inside braces
  // Syntax: {"metric.name", foo="bar"}
  const allMatchers: string[] = [];

  // Add quoted metric name as first element
  allMatchers.push(`"${metric}"`);

  // Add other filters
  filters.forEach((f) => {
    allMatchers.push(`${formatLabelName(f.label)}${f.operator}"${f.values.join('|')}"`);
  });

  return `{${allMatchers.join(', ')}}`;
}

/**
 * Apply Range to a query.
 */
export function applyRange(query: string, range: BuilderState['range']) {
  if (!range) return query;
  return `${range.functionName}(${query}[${range.interval}])`;
}

/**
 * Apply Aggregation to a query.
 */
function applyAggregation(query: string, aggregation: Aggregation) {
  if (!aggregation) return query;

  const { kind, labels } = aggregation;
  const functionName = CALCULATION_KIND_TO_FUNCTION[kind];

  if (labels.length === 0) {
    return `${functionName}(${query})`;
  } else {
    const formattedLabels = labels.map(formatLabelName).join(', ');
    return `${functionName} by (${formattedLabels}) (${query})`;
  }
}

/**
 * Converts a Filter to a Matcher.
 */
export function filterToMatcher(filter: Filter): Matcher {
  let operation: number = ParserTerms.EqlSingle;
  if (filter.operator === FILTER_OPERATORS.NotEqual) {
    operation = ParserTerms.Neq;
  } else if (filter.operator === FILTER_OPERATORS.RegexEqual) {
    operation = ParserTerms.EqlRegex;
  } else if (filter.operator === FILTER_OPERATORS.RegexNotEqual) {
    operation = ParserTerms.NeqRegex;
  }
  return new Matcher(operation, filter.label, filter.values.join('|'));
}

/**
 * Check if a query is fully supported by the builder.
 */
export function isQuerySupportedByBuilder(query?: string) {
  if (!query || query?.trim() === '') {
    return true;
  }

  const ast = parser.parse(query);

  // Check if the query has a single vector selector
  const validateVectorSelector = (node: SyntaxNode) => {
    if (node.type.is(VectorSelector)) {
      return true;
    }
    return false;
  };

  const validateFunctionCall = (node: SyntaxNode) => {
    // Check if the function call is one of the supported functions
    if (node.type.is(FunctionCall)) {
      const functionName = getFunctionName(node, query);
      if (
        functionName &&
        (RANGE_FUNCTIONS.includes(functionName as RangeFunction) || HISTOGRAM_FUNCTIONS.includes(functionName))
      ) {
        return true;
      }
    }
    return false;
  };

  const validateAggregateExpr = (node: SyntaxNode) => {
    // Ensure the expression has a valid body
    if (node.type.is(AggregateExpr)) {
      const aggOpNode = node.getChild(AggregateOp);
      // TODO: support other aggregate functions
      if (!aggOpNode) {
        return false;
      }

      const aggOpFn = query.substring(aggOpNode.from, aggOpNode.to);
      if (!AGGREGATION_FUNCTIONS.includes(aggOpFn)) {
        return false;
      }
      // Ensure the expression is a BY expression TODO: support without
      const aggModifier = node.getChild(AggregateModifier);
      if (aggModifier && !aggModifier?.firstChild?.type.is(By)) {
        return false;
      }
      const functionCallBody = node.getChild(FunctionCallBody);
      if (functionCallBody?.firstChild?.type.is(VectorSelector)) {
        return validateVectorSelector(functionCallBody.firstChild);
      } else if (functionCallBody?.firstChild?.type.is(FunctionCall)) {
        return validateFunctionCall(functionCallBody.firstChild);
      }
    }
    return false;
  };

  const firstNode = ast.topNode.firstChild;
  if (!firstNode) return true;

  if (firstNode.type.is(VectorSelector)) {
    return validateVectorSelector(firstNode);
  }

  if (firstNode.type.is(FunctionCall)) {
    return validateFunctionCall(firstNode);
  }

  if (firstNode.type.is(AggregateExpr)) {
    return validateAggregateExpr(firstNode);
  }

  return false;
}

/**
 * Check if a FunctionCall node has $__interval variable in its MatrixSelector
 */
export function hasIntervalVariable(node: SyntaxNode, query: string): boolean {
  const functionCallBodyNode = node.getChild(ParserTerms.FunctionCallBody);
  if (!functionCallBodyNode) return false;

  // Iterate through children of the function call body to find a MatrixSelector
  const currentBodyCursor = functionCallBodyNode.cursor();
  do {
    const currentChildNode = currentBodyCursor.node;
    if (currentChildNode.type.is(MatrixSelector)) {
      // Check for $__interval within this MatrixSelector
      const intervalNode = currentChildNode.getChild(TemplateVariable);
      if (intervalNode && query.substring(intervalNode.from, intervalNode.to) === '$__interval') {
        return true;
      }
    }
  } while (currentBodyCursor.next());

  return false;
}

export function convertStringToQuantile(str: string): number {
  // Any digits and decimal points are valid
  const isValid = /^[0-9.]+$/.test(str);
  const number = parseFloat(str);
  // number must be between 0 and 100
  if (number < 0 || number > 100 || isNaN(number) || !isValid) {
    throw new Error('Quantile must be between 0 and 100');
  }
  const numDecimalDigits = str.includes('.') ? (str.split('.')?.[1]?.length ?? 0) : 0;
  const quantile = number / 100;
  return parseFloat(quantile.toFixed(numDecimalDigits + 2));
}
